/**
 * MachinePage — Machine picker
 * Lists all machines assigned to the user.
 * Selecting one navigates to /app/submachine/:machineId/:machineId
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import client from '../API/API';

const Icon = ({ d, size = 16, color = 'currentColor', sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS = {
  search:  'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z',
  arrow:   'M5 12h14 M12 5l7 7-7 7',
  machine: 'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
};

function MachineCard({ machine, index, onClick }) {
  const isOnline = machine.status === 'online' || machine.Machines_status === 'Active';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.04, 0.4) }}
      onClick={onClick}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-hair)',
        borderRadius: 'var(--r-lg)',
        padding: '18px 20px',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 16,
        boxShadow: 'var(--sh-sm)',
        transition: 'all 0.15s ease',
        userSelect: 'none',
      }}
      whileHover={{ y: -2, boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Icon */}
      <div style={{
        width: 44, height: 44, borderRadius: 'var(--r-md)',
        background: 'var(--accent-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon d={ICONS.machine} size={20} color="var(--accent)" />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 14,
          color: 'var(--ink-900)', marginBottom: 3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {machine.machine_name || machine.machine_id}
        </div>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 11,
          color: 'var(--ink-400)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {machine.machine_id}
          {machine.org_name ? ` · ${machine.org_name}` : ''}
        </div>
      </div>

      {/* Status pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 20,
        background: isOnline ? 'var(--online-bg)' : 'var(--offline-bg, #FDECEA)',
        border: `1px solid ${isOnline ? 'var(--online-border)' : 'var(--offline-border, #F5C0BC)'}`,
        fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500,
        color: isOnline ? 'var(--online)' : 'var(--offline, #C42B1C)',
        flexShrink: 0,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: isOnline ? 'var(--online)' : 'var(--offline, #C42B1C)',
          animation: isOnline ? 'atoms-pulse 2s infinite' : 'none',
        }} />
        {isOnline ? 'Online' : 'Offline'}
      </div>

      {/* Arrow */}
      <Icon d={ICONS.arrow} size={16} color="var(--ink-300)" />
    </motion.div>
  );
}

const MachinePage = () => {
  const navigate  = useNavigate();
  const [machines, setMachines] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await client.get('/api/machines/');
        setMachines(res.data.machines || []);
      } catch (err) {
        if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
      } finally { setLoading(false); }
    };
    load();
  }, [navigate]);

  const filtered = machines.filter(m => {
    const q = search.toLowerCase();
    return (
      (m.machine_name || '').toLowerCase().includes(q) ||
      (m.machine_id   || '').toLowerCase().includes(q) ||
      (m.org_name     || '').toLowerCase().includes(q)
    );
  });

  const handleSelect = (machine) => {
    navigate(`/app/submachine/${machine.machine_id}/${machine.machine_id}`);
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>

      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        style={{ marginBottom: 24 }}
      >
        <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 22, color: 'var(--ink-900)', marginBottom: 4 }}>
          Machines
        </div>
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)' }}>
          {loading ? 'Loading…' : `${machines.length} machine${machines.length !== 1 ? 's' : ''} assigned to your account`}
        </div>
      </motion.div>

      {/* ── Search ── */}
      {!loading && machines.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.1 }}
          style={{ position: 'relative', marginBottom: 20 }}
        >
          <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <Icon d={ICONS.search} size={15} color="var(--ink-400)" />
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, ID or organisation…"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 14px 10px 36px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-900)',
              outline: 'none', transition: 'border-color 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent)'}
            onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'}
          />
        </motion.div>
      )}

      {/* ── Loading skeletons ── */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              height: 80, borderRadius: 'var(--r-lg)',
              background: 'linear-gradient(90deg, #F6F7F9 25%, #ECEDF1 50%, #F6F7F9 75%)',
              backgroundSize: '200% 100%',
              animation: 'atoms-shimmer 1.4s ease infinite',
            }} />
          ))}
        </div>
      )}

      {/* ── Machine list ── */}
      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((m, i) => (
            <MachineCard key={m.machine_id} machine={m} index={i} onClick={() => handleSelect(m)} />
          ))}
        </div>
      )}

      {/* ── No results ── */}
      {!loading && machines.length > 0 && filtered.length === 0 && (
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--ink-700)', marginBottom: 6 }}>No machines found</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)' }}>Try a different search term.</div>
        </div>
      )}

      {/* ── No machines at all ── */}
      {!loading && machines.length === 0 && (
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚙️</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--ink-700)', marginBottom: 6 }}>No machines assigned</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)' }}>Contact your administrator to get access.</div>
        </div>
      )}

    </div>
  );
};

export default MachinePage;
